import { LoggedInUserService } from '../user/logged-in-user.service';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { merge } from 'lodash';
import { ClientConstants } from '../../constants/ClientConstants';
import { OfflineStatus, PageDownloadProgress, LastTileSyncDetails, User } from '@formbird/types';
import { IApplicationState } from '../../redux/state/application.state';
import { AppStore } from '../../redux/store/app.store';
import { offlineSetOfflineStatus } from '../../redux/actions/offline.actions';
import { KeyValueStorageService} from '../key-value-storage/key-value-storage.service';
import { SessionTestService } from '../session/session-test.service';
import { IndexedDBConstants } from '@formbird/indexed-db';

@Injectable()
export class OfflineStatusService {
  private loggedInUserService = inject(LoggedInUserService);
  private appStore = inject<AppStore<IApplicationState>>(AppStore);
  private keyValueStorageService = inject(KeyValueStorageService);
  private sessionTestService = inject(SessionTestService);

  public offlineStatus: OfflineStatus;
  private offlineStatusSubject: Subject<OfflineStatus>;
  offlineStatus$: Observable<OfflineStatus>;
  pageDownloadProgress: PageDownloadProgress;

  constructor() {

    this.offlineStatus = {
      unsyncedCount: 0,
      connected: true,
      incomingCacheCount: 0,
      maxCacheCount: 0,
      currentCacheCount: 0,
      cacheLoading: false,
      pendingDocumentCount: 0,
      errorCacheCount: 0,
      cachingApp: false,
      initialCachingComplete: true,
      connectionText: null,
      currentStaticResourceCount: 0,
      totalStaticResourceCount: 0,
      tileServerCacheCount: 0,
      tileServerMaxCacheCount: 0,
      errorCacheAttachedFile: [],
      errorOfflineChangesSyncDocs: [],
      maxCacheAttachedFileCount: 0,
      currentCacheAttachedFileCount: 0,
      enabled: null,
      operateOnline: true,
      pendingUploadCount: 0
    };

    this.offlineStatusSubject = new Subject<OfflineStatus>();
    this.offlineStatus$ = this.offlineStatusSubject.asObservable();

    this.pageDownloadProgress = {
      pageNumber: 0,
      loaded: 0,
      total: 0
    };
        
    this.loggedInUserService.getUserConfig();
  }

  async initializeOfflineCounts() {
    if (!this.isCachingEnabled()) {
      return;
    }

    const userId = this.loggedInUserService.getUserId();
    if (!userId) {
      return;
    }

    try {
      const dbName = IndexedDBConstants.FT_DOCUMENTS_DB_NAME_PREFIX + userId;
      const getCount = (storeName: string): Promise<number> => {
        return new Promise((resolve) => {
          const request = indexedDB.open(dbName);
          request.onsuccess = (event: any) => {
            const db = event.target.result;
            if (db.objectStoreNames.contains(storeName)) {
              try {
                const transaction = db.transaction(storeName, 'readonly');
                const store = transaction.objectStore(storeName);
                const countRequest = store.count();
                countRequest.onsuccess = () => {
                  const count = countRequest.result;
                  db.close();
                  resolve(count);
                };
                countRequest.onerror = () => {
                  db.close();
                  resolve(0);
                };
              } catch (e) {
                db.close();
                resolve(0);
              }
            } else {
              db.close();
              resolve(0);
            }
          };
          request.onerror = () => resolve(0);
        });
      };

      const documentsCount = await getCount(IndexedDBConstants.DOCUMENT_TABLE_NAME);
      const offlineFilesCount = await getCount(IndexedDBConstants.OFFLINE_FILE_TABLE_NAME);

      this.offlineStatus.currentCacheCount = documentsCount;
      this.offlineStatus.currentCacheAttachedFileCount = offlineFilesCount;

      // Keep max counts as at least the current counts if we don't have better info
      if (this.offlineStatus.maxCacheCount < documentsCount) {
        this.offlineStatus.maxCacheCount = documentsCount;
      }
      if (this.offlineStatus.maxCacheAttachedFileCount < offlineFilesCount) {
        this.offlineStatus.maxCacheAttachedFileCount = offlineFilesCount;
      }

      // Cache storage counts
      if ('caches' in window) {
        const tilesCacheName = `tiles-${userId}`;

        const tilesCache = await caches.open(tilesCacheName);
        const tilesKeys = await tilesCache.keys();
        this.offlineStatus.tileServerCacheCount = tilesKeys.length;
        if (this.offlineStatus.tileServerMaxCacheCount < tilesKeys.length) {
          this.offlineStatus.tileServerMaxCacheCount = tilesKeys.length;
        }

        const originWithSlash = `${window.location.origin}/`;

        const staticCacheNames = [
          'formbird-vendor-libs',
          `workbox-precache-v2-${originWithSlash}`,
          'formbird-dexie-addons'
        ];

        let staticResourceCount = 0;

        for (const cacheName of staticCacheNames) {
          try {
            const cache = await caches.open(cacheName);
            const keys = await cache.keys();
            staticResourceCount += keys.length;
          } catch (e) {
            // Ignore errors for individual caches and continue
          }
        }

        this.offlineStatus.currentStaticResourceCount = staticResourceCount;
        if (this.offlineStatus.totalStaticResourceCount < staticResourceCount) {
          this.offlineStatus.totalStaticResourceCount = staticResourceCount;
        }
      }

      this.setOfflineStatusToLocalStorage();
    } catch (error) {
      console.error('Error initializing offline counts:', error);
    }
  }

  isInitialCachingComplete() {
    return this.offlineStatus.initialCachingComplete || this.loggedInUserService.getUserConfigItem('initialCachingComplete') === true;
  }

  isOfflineMode() {
   let operateOnline = this.loggedInUserService.getUserConfigItem('operateOnline');
    if (operateOnline === undefined || operateOnline === null)
      return this.isCachingEnabled() && this.isInitialCachingComplete();
    else {
      return this.isCachingEnabled() && this.isInitialCachingComplete() && !this.shouldOperateOnline();
    }
  }

  getLastSyncDate(): number {
    // initial value is zero see this.loggedInUserService;
    // fetch directly from the User settings so it will still work across logins
    return this.loggedInUserService.getUserConfigItem('lastSyncDate');
  }

  setLastSyncDate(document) {
    // update the last sync date if the date is later than the stored value. This will be used to sync new objects
    // when data is saved or the app is loaded again. The sync will only happen from the last sync date value
    if (document && document.systemHeader && document.systemHeader.serverUpdatedDate) {
      // use the number of values since the epoch (1/1/1970) in milliseconds, as returned by the valueOf() function,
      // so it can be used to easily compare
      const syncDate = new Date(document.systemHeader.serverUpdatedDate).valueOf();
      if (syncDate > this.getLastSyncDate()) {
        this.loggedInUserService.setUserConfigItem('lastSyncDate', syncDate);
      }
    }
  }

  async setOfflineStatusToLocalStorage() {
    await this.keyValueStorageService.setItem(ClientConstants.LOCAL_STORAGE_OFFLINE_STATUS_KEY, JSON.stringify(this.offlineStatus));    
    //used to update loggedInUserService.userConfig property for the offine status change
    await this.loggedInUserService.getUserConfig();

    // publish offline status subject.
    // This can be changed to publish the subject in other tabs when the data changes in localStorage
    this.offlineStatusSubject.next(this.offlineStatus);
    this.appStore.dispatch(offlineSetOfflineStatus(this.offlineStatus));
    
  }

  /**
   * Displays the loading cache status in the page
   * @param isCacheLoading
   * returns true if all pending documents had been cached.
   */
  setCacheLoadingStatus(isCacheLoading) {
    this.offlineStatus.cacheLoading = isCacheLoading;
    this.setOfflineStatusToLocalStorage();
    return this.offlineStatus.currentCacheCount === this.offlineStatus.maxCacheCount;
  }

  isCacheLoading() {
    return this.offlineStatus.cacheLoading;
  }

  setConnected() {
    this.offlineStatus.connected = true;
    this.offlineStatus.connectionText = 'Online';
    this.setOfflineStatusToLocalStorage();    
  }

  setDisconnected() {
    this.offlineStatus.connected = false;
    this.offlineStatus.connectionText = 'Offline';
    this.setOfflineStatusToLocalStorage();
  }

  addToMaxCacheCount(count) {
    if (count) {
      this.offlineStatus.maxCacheCount += count;
      this.setOfflineStatusToLocalStorage();
    }
  }

  async setOperateOnline(operateOnline: boolean) {
    const opOnline = operateOnline ? null: false;
    this.offlineStatus.operateOnline = opOnline
    await this.loggedInUserService.setUserConfigItems([{'operateOnline' : opOnline}]);
    await this.setOfflineStatusToLocalStorage();
  }

  shouldOperateOnline() {
    let operateOnline = this.loggedInUserService.getUserConfigItem('operateOnline');
    if (!operateOnline && operateOnline !== false) {
      operateOnline = this.offlineStatus.operateOnline  !== false && this.offlineStatus.connected;
    }

    return operateOnline;
  }

  ignoreCacheDocument(doc) {
    this.offlineStatus.currentCacheCount++;
    this.offlineStatus.errorCacheCount++;

    this.setOfflineStatusToLocalStorage();
  }

  getErrorCacheCount() {
    return this.offlineStatus.errorCacheCount;
  }

  initialCacheQuerySuccess(document) {
    this.offlineStatus.currentCacheCount++;
    this.checkCurrentCacheCount();

    this.setLastSyncDate(document);
  }

  checkCurrentCacheCount() {
    if (this.offlineStatus.currentCacheCount >= this.offlineStatus.maxCacheCount) {
      this.offlineStatus.currentCacheCount = 0;
    }
    this.setOfflineStatusToLocalStorage();
  }

  setInitialCachingComplete(complete) {
    this.offlineStatus.initialCachingComplete = (complete === true);
    this.setOfflineStatusToLocalStorage();
  }

  setPendingDocumentCount(pendingCount) {

    this.offlineStatus.pendingDocumentCount = pendingCount;
    this.loggedInUserService.setUserConfigItem('offlinePendingDocumentsCount', pendingCount);
    this.setOfflineStatusToLocalStorage();
  }

  incPendingDocumentCount() {

    this.offlineStatus.pendingDocumentCount++;
    this.loggedInUserService.setUserConfigItem('offlinePendingDocumentsCount', this.offlineStatus.pendingDocumentCount);
    this.setOfflineStatusToLocalStorage();
  }

  decPendingDocumentCount() {

    this.offlineStatus.pendingDocumentCount--;
    this.loggedInUserService.setUserConfigItem('offlinePendingDocumentsCount', this.offlineStatus.pendingDocumentCount);
    this.setOfflineStatusToLocalStorage();
  }

  setPendingUploadCount(count: number) {
    this.offlineStatus.pendingUploadCount = count;
    this.setOfflineStatusToLocalStorage();
  }

  incPendingUploadCount() {
    this.offlineStatus.pendingUploadCount++;
    this.setOfflineStatusToLocalStorage();
  }

  decPendingUploadCount() {
    if (this.offlineStatus.pendingUploadCount > 0) {
      this.offlineStatus.pendingUploadCount--;
      this.setOfflineStatusToLocalStorage();
    }
  }

  isConnected() {
    return this.offlineStatus.connected;
  }

  setCachingAppStatus(isCachingApp) {
    this.offlineStatus.cachingApp = isCachingApp;
    this.setOfflineStatusToLocalStorage();
  }

  setCurrentStaticResourceCount(count: number) {
    this.offlineStatus.currentStaticResourceCount = count;
    this.setOfflineStatusToLocalStorage();
  }

  setTotalStaticResourceCount(count: number) {
    this.offlineStatus.totalStaticResourceCount = count;
    this.setOfflineStatusToLocalStorage();
  }

  mergeOfflineStatusData(offlineStatus) {
    merge(this.offlineStatus, offlineStatus);
    this.setOfflineStatusToLocalStorage();
  }
  
  checkServerConnectionStatus(){
    const _self = this;
    
     return new Promise<any>((resolve, reject) => {
      if (this.isOfflineMode()){
         resolve(true);
      } else {
        this.sessionTestService
          .testSession()
          .then(
            res => {
              if (res===""){
                _self.setDisconnected();
                resolve(false);
              } else {
                _self.setConnected();
                resolve(true);
              }

            },
            err => {
              if (err.status === 401) {
                _self.setConnected();
                resolve(true);
              } else {
                _self.setDisconnected();
                resolve(false);
              }
            }
          );
      }
    });
  }

  getOfflineLoginConnectionPollTime() {
    const config = (<any>window).ftClientConfig;
    return config.offlineLoginConnectionPollTime || 1000;
  }


  setMaxCacheCount(count) {
    if (count || count === 0) {
      this.offlineStatus.maxCacheCount = count;
      this.setOfflineStatusToLocalStorage();
    }
  }

  setCachingEnabledStatus(enabled: boolean) {
    this.offlineStatus.enabled = enabled;
    this.loggedInUserService.setUserConfigItem('cachingEnabled', enabled);
  }

  isCachingEnabled() {
    if (this.offlineStatus.enabled === null) { // enabled status is null, checking localstorage
      this.offlineStatus.enabled = this.loggedInUserService.getUserConfigItem('cachingEnabled') === true;
    }

    return this.offlineStatus.enabled;
  }

  async checkErrorOfflineChangesSyncDocs() {
    const usderId = await this.loggedInUserService.getUserId();
    let userConfig: any = await this.keyValueStorageService.getItem(usderId);
    if (userConfig) {
      userConfig = JSON.parse(userConfig);
      if (userConfig.ErrorOfflineChangesSyncDocs) {
        this.offlineStatus.errorOfflineChangesSyncDocs = userConfig.ErrorOfflineChangesSyncDocs;
      }
    }
  }

  publishOfflineStatus() {
    // publish offline status subject.
    // This can be changed to publish the subject in other tabs when the data changes in localStorage
    this.offlineStatusSubject.next(this.offlineStatus);
    this.appStore.dispatch(offlineSetOfflineStatus(this.offlineStatus));
  }
  async getOfflineStatusFromKeyValueStorage() {
    this.offlineStatus = await this.keyValueStorageService.getItem(ClientConstants.LOCAL_STORAGE_OFFLINE_STATUS_KEY);
    return this.offlineStatus;
  }

  async loadOfflineStatusFromKeyValueStorage() {
    try {
      await this.getOfflineStatusFromKeyValueStorage();
      this.publishOfflineStatus();

      return this.offlineStatus;
    } catch (error) {
      console.error('Error loading offline status from key value storage:', error);
      return null;
    }
  }

  setLastTileSyncDetails(lastSyncDetails: LastTileSyncDetails) {
    this.loggedInUserService.setUserConfigItem('lastSyncTileDetails', lastSyncDetails);
  }

  getLastTileSyncDetails(): LastTileSyncDetails {
      return this.loggedInUserService.getUserConfigItem('lastSyncTileDetails') || { lastSyncX: 0, lastSyncY: 0 };
  }

  setAssetsCachingCompleted(completed: boolean) {
    this.offlineStatus.initialAssetsCachingCompleted = completed;
    this.loggedInUserService.setUserConfigItem('initialAssetsCachingCompleted', completed);
  }

  setDataCachingCompleted(completed: boolean) {
    if (completed === true || this.offlineStatus.initialDataCachingCompleted !== true) {
      console.log('Call set data caching completed: ', completed);
      this.offlineStatus.initialDataCachingCompleted = completed;
      this.loggedInUserService.setUserConfigItem('initialDataCachingCompleted', completed);
    }  
  }

  isDataCachingCompleted() {
    
    if (this.offlineStatus.initialDataCachingCompleted === null) {
      // initialDataCachingCompleted status is null, checking localstorage
      this.offlineStatus.initialDataCachingCompleted = this.loggedInUserService.getUserConfigItem('initialDataCachingCompleted') === true;
    }

    return this.offlineStatus.initialDataCachingCompleted;
  }

  setMapTilesCachingCompleted(completed: boolean) {
    this.offlineStatus.initialMapTilesCachingCompleted = completed;
    this.loggedInUserService.setUserConfigItem('initialMapTilesCachingCompleted', completed);
  }

  checkCachingCompleted() {
    
    console.debug('offline status data: ', this.offlineStatus);
    
    const cacheLoading = this.offlineStatus.cacheLoading;
    const initialAssetsCachingCompleted = this.offlineStatus.initialAssetsCachingCompleted || this.loggedInUserService.getUserConfigItem('initialAssetsCachingCompleted');
    const initialDataCachingCompleted = this.offlineStatus.initialDataCachingCompleted || this.loggedInUserService.getUserConfigItem('initialDataCachingCompleted');
    const initialMapTilesCachingCompleted = this.offlineStatus.initialMapTilesCachingCompleted || this.loggedInUserService.getUserConfigItem('initialMapTilesCachingCompleted');

    if (
      cacheLoading &&
      initialAssetsCachingCompleted &&
      initialDataCachingCompleted &&
      initialMapTilesCachingCompleted
    ) {
      this.setCacheLoadingStatus(true);
      this.setInitialCachingComplete(true);
    }
  }

   async postMessageToServiceWorker(operateOnline) {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const registration = await navigator?.serviceWorker?.ready;
        registration?.active?.postMessage({
            type: 'OPERATE_ONLINE',
            operateOnline
        });
      }
    }
     
    }
}
